/**
 * EventWebhookAdminGuard — for event apps (Calendly / Acuity), gates content
 * that depends on the incoming-sync webhook owner. When an existing webhook
 * was configured by another org user, the current user's edits would write to
 * the wrong personal doc and be ignored. This dims the wrapped content and
 * overlays a "Change Me as App Admin" takeover prompt, mirroring the alert in
 * the Incoming Sync settings page.
 *
 * No-op pass-through for non-event apps or when the current user already owns
 * the webhook.
 */
import { useState, type ReactNode } from 'react';
import { ShieldCheck, AlertTriangle, UserCog, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/common/confirm-dialog';
import { useEventWebhookOwnership } from '@/hooks/use-event-webhook-ownership';

export function EventWebhookAdminGuard({ children }: { children: ReactNode }) {
  const { ownedByOther, configurer, becomingAdmin, becomeAdmin } = useEventWebhookOwnership();
  const [showConfirm, setShowConfirm] = useState(false);

  if (!ownedByOther) return <>{children}</>;

  const who = configurer?.name?.trim() || configurer?.email || 'another team member';

  const handleConfirm = async () => {
    try {
      await becomeAdmin();
      toast.success('You are now the integration admin for incoming events');
      setShowConfirm(false);
    } catch (error) {
      console.error('Failed to take over as admin:', error);
      toast.error(
        error instanceof Error ? error.message : 'Failed to switch integration admin'
      );
    }
  };

  return (
    <div className="relative flex flex-col h-full">
      <div className="flex flex-col h-full min-h-0 pointer-events-none opacity-50 select-none">
        {children}
      </div>

      <div className="absolute inset-0 z-50 flex items-center justify-center px-6 pointer-events-auto">
        <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px]" />
        <div className="relative rounded-lg border border-amber-400/40 bg-background shadow-lg p-5 max-w-md text-center">
          <ShieldCheck className="h-6 w-6 text-amber-600 dark:text-amber-400 mx-auto mb-2" />
          <p className="text-sm font-medium">
            Field mapping is managed by{' '}
            <span className="text-primary">{who}</span>
          </p>
          {configurer?.email && configurer.name?.trim() && configurer.name !== configurer.email && (
            <p className="text-[11px] text-muted-foreground mt-0.5">{configurer.email}</p>
          )}
          <div className="mt-3 rounded-lg border border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 p-3 text-xs text-amber-800 dark:text-amber-300 flex items-start gap-2 text-left">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <span>
              Taking over will stop the existing field mapping and sync configured by {who}, and
              route future events to your own configuration.
            </span>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowConfirm(true)}
            disabled={becomingAdmin}
            className="mt-3 border-amber-400/60"
          >
            {becomingAdmin ? (
              <>
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
                Switching...
              </>
            ) : (
              <>
                <UserCog className="h-3.5 w-3.5 mr-1.5" />
                Change Me as App Admin
              </>
            )}
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={showConfirm}
        onClose={() => setShowConfirm(false)}
        onConfirm={handleConfirm}
        loading={becomingAdmin}
        title="Change Me as App Admin"
        description={`This will stop the field mapping and incoming sync configured by ${who}. A new webhook will be created under your account and future events will use your configuration.`}
        confirmText="Yes, make me admin"
        icon={<UserCog className="h-6 w-6 text-primary" />}
      />
    </div>
  );
}
